import React, {useEffect, useMemo, useRef, useState} from 'react';
import {CircularProgress, List, ListItem, makeStyles, Paper} from '@material-ui/core';
import cn from 'classnames';
import * as yup from 'yup';
import useNyField from 'nyse-web-tools-common/lib/react/hooks/useNyField/useNyField';
import {testIsRequired} from 'nyse-web-tools-common/lib/utils';
import NyValidationTooltip from 'nyse-web-tools-common/lib/react/form/NyValidationTooltip/NyValidationTooltip';
import {useSymbolTypeahead} from '../../hooks/useSymbolTypeahead';
import {SymbolTypeaheadInputProps} from '../../types/symbolTypeahead.types';

const useStyles = makeStyles((theme) => ({
    input: (props: any) => ({
        alignItems: 'center',
        backgroundColor: props.disabled || props.readOnly ? 'rgba(210, 212, 211, 0.25)' : 'white',
        borderColor: props.valid === false ? '#c9001a' : 'transparent',
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'row',
        height: '26px !important',
        lineHeight: 1,
        minWidth: 0,
        padding: 0,
        textIndent: '0.5em',
        transition: 'border-color ease-in-out .15s,box-shadow ease-in-out .15s'
    }),
    inputWithIcon: {
        paddingRight: '30px'
    },
    listItem: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        },
        cursor: 'pointer',
        fontSize: '0.9rem',
        padding: theme.spacing(1, 1.5)
    },
    loading: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(2)
    },
    loadingIcon: {
        alignItems: 'center',
        display: 'flex',
        position: 'absolute',
        right: '8px',
        top: '0',
        bottom: '0'
    },
    noResults: {
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
        padding: theme.spacing(2),
        textAlign: 'center'
    },
    paper: {
        borderRadius: '4px',
        boxShadow: '0 3px 6px rgba(0,0,0,.16), 0 3px 6px rgba(0,0,0,.23)',
        left: 0,
        marginTop: '4px',
        maxHeight: 300,
        overflow: 'auto',
        position: 'absolute',
        right: 0,
        zIndex: 1500
    },
    root: {
        position: 'relative'
    },
    symbolDescription: {
        color: '#666',
        fontSize: '0.8rem'
    },
    symbolPrimary: {
        fontWeight: 500
    }
}));

/**
 * SymbolTypeaheadInput Component
 *
 * A custom typeahead component for symbol selection with real-time search.
 *
 * Features:
 * - Real-time symbol search with debouncing (300ms)
 * - Displays symbol + company description
 * - Automatic uppercase input formatting
 * - Formik validation integration via useNyField
 * - Loading indicator during search
 * - Error handling and empty state
 * - Click-outside-to-close dropdown
 */
export const SymbolTypeaheadInput: React.FC<SymbolTypeaheadInputProps> = React.memo((props) => {
    const {
        disabled = false,
        limit = 10,
        maxLength = 24,
        name,
        placeholder = 'Search symbol...',
        readOnly = false,
        required = false
    } = props;

    const fieldCtx = useNyField(
        props,
        useMemo(
            () =>
                yup
                    .mixed()
                    .test({
                        test: testIsRequired({
                            disabled,
                            readOnly,
                            required
                        })
                    }),
            [disabled, readOnly, required]
        )
    );

    const classes = useStyles({...props, valid: fieldCtx.valid});

    const [inputValue, setInputValue] = useState(fieldCtx.value || '');
    const [showDropdown, setShowDropdown] = useState(false);
    const justSelectedRef = useRef(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const {error, isLoading, results} = useSymbolTypeahead(inputValue, {limit});

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Show dropdown when there are results, but not if we just selected something
    useEffect(() => {
        if (!justSelectedRef.current) {
            setShowDropdown(results.length > 0 && inputValue.length > 0);
        }
    }, [results, inputValue]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toUpperCase();
        justSelectedRef.current = false; // Reset when user types
        setInputValue(value);
        fieldCtx.onChange(value);
    };

    const handleSelectSymbol = (symbol: string) => {
        justSelectedRef.current = true; // Prevent dropdown from reopening
        setInputValue(symbol);
        fieldCtx.onChange(symbol);
        setShowDropdown(false);
    };

    const handleFocus = () => {
        if (results.length > 0 && inputValue.length > 0) {
            setShowDropdown(true);
        }
    };

    return (
        <NyValidationTooltip validation={fieldCtx.validation}>
            <div className={classes.root} ref={wrapperRef}>
                <input
                    autoComplete='off'
                    className={cn('form-control', classes.input, {[classes.inputWithIcon]: isLoading})}
                    data-disabled={disabled}
                    data-e2e-tag={props['data-e2e-tag']}
                    data-read-only={readOnly}
                    data-valid={fieldCtx.valid}
                    data-value={inputValue}
                    disabled={disabled}
                    maxLength={maxLength}
                    name={name}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    required={required}
                    type='text'
                    value={inputValue}
                />
                {isLoading && (
                    <div className={classes.loadingIcon}>
                        <CircularProgress size={14} />
                    </div>
                )}
                {showDropdown && (
                    <Paper className={classes.paper}>
                        {isLoading ? (
                            <div className={classes.loading}>
                                <CircularProgress size={24} />
                            </div>
                        ) : error ? (
                            <div className={classes.noResults}>Error loading symbols</div>
                        ) : results.length === 0 ? (
                            <div className={classes.noResults}>No symbols found</div>
                        ) : (
                            <List dense>
                                {results.map((result) => (
                                    <ListItem
                                        className={classes.listItem}
                                        key={result.symbol}
                                        onClick={() => handleSelectSymbol(result.symbol)}
                                    >
                                        <div>
                                            <div className={classes.symbolPrimary}>{result.symbol}</div>
                                            {result.description && (
                                                <div className={classes.symbolDescription}>
                                                    {result.description}
                                                </div>
                                            )}
                                        </div>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                )}
            </div>
        </NyValidationTooltip>
    );
});
